import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { formatDate } from '../../utils/helper';
import Loader from '../../utils/Loader';
import EmptyMessage from '../../utils/EmptyMessage';
import { Modal } from 'react-bootstrap';
import ImageUploader from '../../utils/FileUploader/ImageUploader';
import ErrorMessage from '../../utils/ErrorMessage';

const IMAGE_EXTENSIONS = [
	'apng',
	'avif',
	'bmp',
	'gif',
	'jpeg',
	'jpg',
	'png',
	'svg',
	'webp',
];

const truncateFilename = filename => {
	// A default parameter would not cover a null filename, which the API does
	// send for some attachments.
	const name = filename || 'Attachment';
	if (name.length <= 20) {
		return name;
	}

	return `${name.substr(0, 9)}...${name.substr(name.length - 7)}`;
};

const isImageAttachment = attachment => {
	if (attachment?.mimeType?.indexOf('image/') === 0) {
		return true;
	}

	const extension = attachment?.filename?.split('.').pop()?.toLowerCase();

	return IMAGE_EXTENSIONS.indexOf(extension) > -1;
};

// Help Scout leaves out the web link for attachments it does not serve
// publicly, such as inline images and freshly uploaded files.
const getAttachmentUrl = attachment => attachment?._links?.web?.href;

const AttachmentChip = ({ attachment }) => {
	const url = getAttachmentUrl(attachment);
	const filename = truncateFilename(attachment?.filename);

	if (!url) {
		return (
			<span
				className="WpdConversationAttachment WpdIsUnavailable"
				title="This attachment is not available to download"
			>
				<i className="wpd-icon wpd-download"></i>
				<span>{filename}</span>
			</span>
		);
	}

	return (
		<a
			href={url}
			target="_blank"
			rel="noreferrer"
			download={attachment?.filename || true}
			className="WpdConversationAttachment"
			title={attachment?.filename}
		>
			<i className="wpd-icon wpd-download"></i>
			<span>{filename}</span>
		</a>
	);
};

const ThreadAttachments = ({ attachments }) => {
	// An image the browser cannot load drops back to a download link, so a
	// blocked or expired URL never leaves a broken thumbnail behind.
	const [unloadable, setUnloadable] = useState([]);
	const [preview, setPreview] = useState(undefined);

	if (!attachments || !attachments.length) {
		return null;
	}

	const items = attachments.map((attachment, index) => ({
		attachment,
		key: `${attachment?.id || 'attachment'}-${index}`,
		url: getAttachmentUrl(attachment),
	}));

	const previewable = items.filter(
		item =>
			item.url &&
			isImageAttachment(item.attachment) &&
			unloadable.indexOf(item.key) === -1
	);
	const listed = items.filter(item => previewable.indexOf(item) === -1);

	return (
		<>
			{previewable.length > 0 && (
				<div className="WpdConversationAttachmentPreviews">
					{previewable.map(item => (
						<button
							type="button"
							className="WpdConversationAttachmentPreview"
							onClick={e => setPreview(item)}
							title={item.attachment?.filename}
							key={item.key}
						>
							<img
								src={item.url}
								alt={item.attachment?.filename || 'Attachment'}
								loading="lazy"
								onError={e =>
									setUnloadable(current =>
										current.concat(item.key)
									)
								}
							/>
						</button>
					))}
				</div>
			)}
			{listed.length > 0 && (
				<div className="WpdConversationAttachmentGroup">
					{listed.map(item => (
						<AttachmentChip
							attachment={item.attachment}
							key={item.key}
						/>
					))}
				</div>
			)}
			<Modal
				show={preview !== undefined}
				onHide={e => setPreview(undefined)}
				centered
				size="lg"
				className="WpdAttachmentPreviewModal"
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{preview?.attachment?.filename || 'Attachment'}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{preview && (
						<img
							src={preview.url}
							alt={preview.attachment?.filename || 'Attachment'}
							className="img-fluid"
						/>
					)}
				</Modal.Body>
				<Modal.Footer>
					<a
						href={preview?.url}
						target="_blank"
						rel="noreferrer"
						download={preview?.attachment?.filename || true}
						className="WpdButton WpdInfoButton WpdFilled"
					>
						<span className="WpdButtonInner">
							<span className="WpdText">Download</span>
							<span className="WpdIcon">
								<i className="wpd-icon wpd-download"></i>
							</span>
						</span>
					</a>
				</Modal.Footer>
			</Modal>
		</>
	);
};

const Conversation = ({
	match,
	loading,
	getConversation,
	conversationDetails,
	sendReply,
	replying,
	replySuccess,
}) => {
	const linkHistory = useHistory();
	const goToPreviousPath = () => {
		linkHistory.goBack();
	};

	const [showModal, setShowModal] = useState(false);
	const [formData, setFormData] = useState({
		message: '',
		message_attachment: [],
	});
	const [messageError, setMessageError] = useState(undefined);

	useEffect(() => {
		match.params.id && getConversation(match.params.id);
	}, []);

	useEffect(() => {
		if (replySuccess) {
			getConversation(match.params.id);
			setFormData({
				message: '',
				message_attachment: [],
			});
			setShowModal(false);
		}
	}, [replySuccess]);

	const handleReply = e => {
		e.preventDefault();
		if (!formData.message.length) {
			setMessageError('Message is required');
			return;
		}
		setMessageError('');
		sendReply(match.params.id, formData);
	};

	return (
		<>
			<section className="WpdMyProductSection">
				<div className="WpdSectionTitleWrap">
					<button
						onClick={goToPreviousPath}
						className="WpdBackButton"
					>
						<span className="WpdIcon">
							<i className="wpd-icon wpd-angle-left"></i>
						</span>
					</button>
					<h4 className="WpdSectionTitle mr-2">
						Threads with WPDeveloper
					</h4>
					<button
						className="WpdButton WpdInfoButton WpdFilled ml-auto"
						data-toggle="modal"
						data-target="#exampleModalCenter"
						onClick={e => setShowModal(true)}
					>
						<span className="WpdButtonInner">
							<span className="WpdText">New Reply</span>
							<span className="WpdIcon">
								<i className="wpd-icon wpd-plus"></i>
							</span>
						</span>
					</button>
				</div>
				{conversationDetails && (
					<div className="WpdSectionTitleWrap flex-column align-items-start">
						<h4 className="WpdSectionSemiTitle">
							<b>Subject:</b>{' '}
							{conversationDetails.conversation.subject}
						</h4>
						<p className="WpdColorSecondary">
							Started on{' '}
							{formatDate(
								conversationDetails.conversation.createdAt
							)}
						</p>
					</div>
				)}
			</section>
			{loading ? (
				<Loader />
			) : conversationDetails &&
			  conversationDetails.threads &&
			  Array.isArray(conversationDetails.threads) ? (
				<section className="WpdConversationSection">
					<div className="WpdConversationWrapper">
						<div className="WpdConversationGroup">
							{conversationDetails.threads.map(
								(thread, index) => {
									return thread.body && thread.body.length ? (
										<div
											className={
												thread.source?.via?.toString() ===
												'user'
													? `WpdSupportConversation`
													: `WpdSelfConversation`
											}
											key={index}
										>
											<>
												<div className="WpdConversationAuthorImage">
													<img
														src={
															thread.createdBy
																?.photoUrl
														}
														alt=""
														className="img-fluid w-100"
													/>
												</div>
												<div className="WpdConversationMessageGroup">
													<div className="WpdConversationMessage">
														<div
															dangerouslySetInnerHTML={{
																__html: thread.body,
															}}
														/>
														<ThreadAttachments
															attachments={
																thread._embedded
																	?.attachments
															}
														/>
													</div>
													<p className="WpdConversationTime">
														{formatDate(
															thread.createdAt,
															false,
															true
														)}
													</p>
												</div>
											</>
										</div>
									) : thread.status &&
									  thread.status.toString() === 'closed' ? (
										<div className="alert alert-secondary py-1 px-3 w-100">
											<small>
												Thread is marked as closed by{' '}
												<strong>{`${thread.createdBy?.first} ${thread.createdBy?.last}`}</strong>{' '}
												in{' '}
												<strong>
													{formatDate(
														thread.createdAt,
														false,
														true
													)}
												</strong>
											</small>
										</div>
									) : (
										''
									);
								}
							)}
						</div>
					</div>
				</section>
			) : (
				<EmptyMessage>No threads found</EmptyMessage>
			)}
			<Modal
				show={showModal}
				onHide={e => setShowModal(false)}
				centered
				size="lg"
				className={replying ? 'is-loading-modal' : ''}
				backdrop={replying ? 'static' : true}
			>
				<Modal.Header closeButton>
					<Modal.Title>Send new reply</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className={`WpdFormWrapper`}>
						<div className="row">
							<div className="col-md-12">
								<div className="WpdFormGroup">
									<label className="WpdFormLabel">
										Message
									</label>
									<textarea
										rows="5"
										className={`form-control ${messageError && 'WpdHasError'}`}
										placeholder="Example: Lorem Ipsum is simply dummy text of the printing and typesetting industry."
										onChange={e =>
											setFormData({
												...formData,
												message: e.target.value,
											})
										}
									></textarea>
									<ErrorMessage error={messageError} />
								</div>
							</div>
							<div className="col-md-12 mb-0">
								<div className="WpdFileBrowseWrapper">
									<label className="WpdFormLabel mr-3">
										Add Attachment:
									</label>
									<ImageUploader
										onFileChange={files =>
											setFormData({
												...formData,
												message_attachment: files,
											})
										}
										multiple={true}
									/>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						className="WpdButton WpdWarningButton ml-auto"
						data-dismiss="modal"
						onClick={e => setShowModal(false)}
					>
						<span className="WpdButtonInner">
							<span className="WpdText">Cancel</span>
						</span>
					</button>
					<button
						className="WpdButton WpdInfoButton WpdFilled ml-2"
						onClick={handleReply}
					>
						<span className="WpdButtonInner">
							<span className="WpdText">Submit</span>
						</span>
					</button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default Conversation;
